import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Link,
    Pagination,
    Switch,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const t = (text) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text);
        } catch (error) {
            return text;
        }
    }

    return text;
};

const emptyPlateEntry = () => ({
    plate: '',
    contract: '',
    note: '',
    parking_slots: '',
    active: true,
});

const emptyForm = {
    id: null,
    type: 'license_plate',
    deviceId: '',
    name: '',
    description: '',
    assignedPersonId: '',
    licensePlateEntries: [],
    quickAddText: '',
    personIds: [],
    sourceLists: [],
};

const resolveEndpoint = (template, id) => (template || '').replace('__LIST_ID__', String(id));

const normalizePlate = (value) => String(value || '').trim().toUpperCase();

const normalizeSeatValue = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') {
        return '';
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0 ? String(Math.trunc(numericValue)) : '';
};

const mapLicensePlateEntries = (entries = []) => {
    if (!Array.isArray(entries)) {
        return [];
    }

    const normalized = [];
    const seen = new Set();
    entries.forEach((entry) => {
        const plate = normalizePlate(entry?.plate || entry?.value || entry);
        if (!plate || seen.has(plate)) {
            return;
        }

        seen.add(plate);
        normalized.push({
            plate,
            note: String(entry?.note || ''),
            contract: String(entry?.contract || ''),
            parking_slots: normalizeSeatValue(entry?.parking_slots ?? entry?.seats),
            active: entry?.active !== false,
        });
    });

    return normalized;
};

const mergeEntries = (currentEntries, incomingEntries) => {
    const merged = new Map();
    mapLicensePlateEntries(currentEntries).forEach((entry) => {
        merged.set(entry.plate, entry);
    });
    mapLicensePlateEntries(incomingEntries).forEach((entry) => {
        merged.set(entry.plate, entry);
    });

    return Array.from(merged.values()).sort((left, right) => left.plate.localeCompare(right.plate));
};

const sourceRestrictionsLabel = (sourceList) => {
    const parkingSlots = sourceList?.restrictions?.parking_slots;
    return Number.isFinite(Number(parkingSlots))
        ? `${sourceList.name || sourceList.id} (${parkingSlots} ${t('capacity')})`
        : (sourceList.name || String(sourceList.id));
};

/**
 * Manage reusable license plate and person authorization lists for GPIO.
 */
export default function GpioAccessListManagementApp({
    lists = [],
    persons = [],
    endpoints = {},
    csrfToken = '',
    licensePlates = [],
    apiGuide = {},
    meta: initialMetaProp,
    devices: initialDevicesProp,
}) {
    const initialMeta = initialMetaProp || {
        current_page: 1,
        last_page: 1,
        per_page: 25,
        total: Array.isArray(lists) ? lists.length : 0,
        search: '',
        type: 'license_plate',
    };
    const devices = Array.isArray(initialDevicesProp) ? initialDevicesProp : [];

    const [rows, setRows] = useState(Array.isArray(lists) ? lists : []);
    const [meta, setMeta] = useState(initialMeta);
    const [activeType, setActiveType] = useState(initialMeta.type || 'license_plate');
    const [tableSearch, setTableSearch] = useState(initialMeta.search || '');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerList, setViewerList] = useState(null);
    const [viewerSearch, setViewerSearch] = useState('');
    const [viewerPage, setViewerPage] = useState(1);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [showExistingPlates, setShowExistingPlates] = useState(false);
    const [showApiInfo, setShowApiInfo] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const importFileRef = useRef(null);

    const sourceOptions = useMemo(
        () => rows
            .filter((row) => row.type === form.type && row.id !== form.id)
            .filter((row) => form.type !== 'person' || Number(row.device_id || 0) === Number(form.deviceId || 0)),
        [form.deviceId, form.id, form.type, rows],
    );

    const filteredPersons = useMemo(() => {
        if (form.type !== 'person') {
            return [];
        }

        const selectedDeviceId = Number(form.deviceId || 0);
        if (!selectedDeviceId) {
            return [];
        }

        return persons.filter((person) => Number(person.device_id || 0) === selectedDeviceId);
    }, [form.deviceId, form.type, persons]);

    const personOptions = useMemo(
        () => persons.slice().sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''))),
        [persons],
    );

    const licensePlateOptions = useMemo(
        () => (Array.isArray(licensePlates) ? licensePlates : [])
            .map((entry) => ({
                id: Number(entry.id || 0),
                plate: normalizePlate(entry.plate),
                note: String(entry.note || ''),
                contract: String(entry.contract || ''),
                active: entry.active !== false,
            }))
            .filter((entry) => entry.plate !== '')
            .sort((left, right) => left.plate.localeCompare(right.plate)),
        [licensePlates],
    );

    const viewerEntries = useMemo(() => {
        const entries = Array.isArray(viewerList?.entries) ? viewerList.entries : [];
        const query = String(viewerSearch || '').trim().toLowerCase();
        if (!query) {
            return entries;
        }

        return entries.filter((entry) => `${entry.label || ''} ${entry.meta || ''}`.toLowerCase().includes(query));
    }, [viewerList, viewerSearch]);

    const viewerPageSize = 24;
    const viewerPageCount = Math.max(1, Math.ceil(viewerEntries.length / viewerPageSize));
    const viewerPageItems = useMemo(() => {
        const start = (viewerPage - 1) * viewerPageSize;
        return viewerEntries.slice(start, start + viewerPageSize);
    }, [viewerEntries, viewerPage]);

    const apiFetch = useCallback(async (url, options = {}) => {
        const response = await fetch(url, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
                ...options.headers,
            },
            ...options,
        });

        if (!response.ok) {
            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            const error = new Error(payload.message || t('Request failed.'));
            error.payload = payload;
            throw error;
        }

        return response.json();
    }, [csrfToken]);

    const fetchLists = useCallback(async ({ page = 1, search = '', type = activeType } = {}) => {
        if (!endpoints.list) {
            return;
        }

        setLoading(true);
        try {
            const url = new URL(endpoints.list, window.location.origin);
            url.searchParams.set('page', page);
            url.searchParams.set('per_page', meta.per_page || 25);
            url.searchParams.set('type', type);
            if (search) {
                url.searchParams.set('search', search);
            }

            const result = await apiFetch(url.toString());
            const nextRows = Array.isArray(result.data)
                ? result.data
                : (Array.isArray(result.lists) ? result.lists : []);
            setRows(nextRows);
            setMeta(result.meta || initialMeta);
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to load access lists.') });
        } finally {
            setLoading(false);
        }
    }, [activeType, apiFetch, endpoints.list, meta.per_page]);

    const openCreateDialog = useCallback(() => {
        setForm({ ...emptyForm, type: activeType });
        setFormErrors({});
        setShowExistingPlates(false);
        setDialogOpen(true);
    }, [activeType]);

    const openEditDialog = useCallback((row) => {
        setForm({
            id: row.id,
            type: row.type,
            deviceId: row.device_id ? String(row.device_id) : '',
            name: row.name || '',
            description: row.description || '',
            assignedPersonId: row.assigned_person_id ? String(row.assigned_person_id) : '',
            licensePlateEntries: mapLicensePlateEntries(row.license_plate_entries || row.values || []),
            quickAddText: '',
            personIds: Array.isArray(row.person_ids) ? row.person_ids : [],
            sourceLists: Array.isArray(row.source_lists)
                ? row.source_lists.map((entry) => ({
                    id: Number(entry.id),
                    enabled: Number.isFinite(Number(entry?.restrictions?.parking_slots ?? entry?.restrictions?.seats)),
                    parkingSlots: normalizeSeatValue(entry?.restrictions?.parking_slots ?? entry?.restrictions?.seats),
                }))
                : [],
        });
        setFormErrors({});
        setShowExistingPlates(false);
        setDialogOpen(true);
    }, []);

    const setPlateEntry = useCallback((index, changes) => {
        setForm((current) => ({
            ...current,
            licensePlateEntries: current.licensePlateEntries.map((entry, entryIndex) => (
                entryIndex === index ? { ...entry, ...changes } : entry
            )),
        }));
    }, []);

    const addQuickEntries = useCallback(() => {
        const quickEntries = form.quickAddText
            .split(/\r?\n/)
            .map((entry) => entry.trim())
            .filter(Boolean)
            .map((plate) => ({ plate }));

        setForm((current) => ({
            ...current,
            licensePlateEntries: mergeEntries(current.licensePlateEntries, quickEntries),
            quickAddText: '',
        }));
    }, [form.quickAddText]);

    const saveList = useCallback(async () => {
        setSaving(true);
        setFormErrors({});

        try {
            const payload = {
                type: form.type,
                device_id: form.type === 'person' ? (form.deviceId || null) : null,
                name: form.name,
                description: form.description,
                assigned_person_id: form.type === 'license_plate' && form.assignedPersonId !== '' ? Number(form.assignedPersonId) : null,
                values: form.type === 'license_plate'
                    ? form.licensePlateEntries.map((entry) => ({
                        plate: normalizePlate(entry.plate),
                        contract: entry.contract,
                        note: entry.note,
                        parking_slots: entry.parking_slots !== '' ? Number(entry.parking_slots) : null,
                        active: entry.active !== false,
                    }))
                    : [],
                person_ids: form.type === 'person' ? form.personIds : [],
                source_lists: form.sourceLists.map((entry) => ({
                    id: Number(entry.id),
                    restrictions: entry.enabled && entry.parkingSlots !== '' ? { parking_slots: Number(entry.parkingSlots) } : {},
                })),
            };

            const url = form.id ? resolveEndpoint(endpoints.update, form.id) : endpoints.create;
            const method = form.id ? 'PUT' : 'POST';
            const result = await apiFetch(url, { method, body: JSON.stringify(payload) });
            const saved = result.data;

            setRows((current) => {
                const next = form.id ? current.map((row) => (row.id === saved.id ? saved : row)) : [...current, saved];
                return next.slice().sort((left, right) => left.name.localeCompare(right.name));
            });
            setDialogOpen(false);
            setFeedback({ severity: 'success', message: form.id ? t('Access list updated.') : t('Access list created.') });
            fetchLists({ page: meta.current_page || 1, search: meta.search || '', type: activeType });
        } catch (error) {
            setFormErrors(error.payload?.errors || {});
            setFeedback({ severity: 'error', message: error.message || t('Failed to save access list.') });
        } finally {
            setSaving(false);
        }
    }, [activeType, apiFetch, endpoints.create, endpoints.update, fetchLists, form, meta.current_page, meta.search]);

    const deleteList = useCallback(async (row) => {
        if (!window.confirm(t('Delete this access list?'))) {
            return;
        }

        try {
            await apiFetch(resolveEndpoint(endpoints.delete, row.id), { method: 'DELETE' });
            setFeedback({ severity: 'success', message: t('Access list deleted.') });
            fetchLists({ page: meta.current_page || 1, search: meta.search || '', type: activeType });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to delete access list.') });
        }
    }, [activeType, apiFetch, endpoints.delete, fetchLists, meta.current_page, meta.search]);

    const importList = useCallback(async () => {
        const file = importFileRef.current?.files?.[0];
        if (!file) {
            setFeedback({ severity: 'error', message: t('Select a CSV file first.') });
            return;
        }

        const body = new FormData();
        body.append('file', file);
        body.append('name', form.name);
        body.append('description', form.description);

        setSaving(true);
        try {
            const result = await apiFetch(endpoints.import, { method: 'POST', body });
            setImportOpen(false);
            setFeedback({ severity: 'success', message: result.message || t('Access list imported.') });
            fetchLists({ page: 1, search: meta.search || '', type: 'license_plate' });
        } catch (error) {
            setFeedback({ severity: 'error', message: error.message || t('Failed to import access list.') });
        } finally {
            setSaving(false);
        }
    }, [apiFetch, endpoints.import, fetchLists, form.description, form.name, meta.search]);

    const openViewer = useCallback((row) => {
        setViewerList(row);
        setViewerSearch('');
        setViewerPage(1);
        setViewerOpen(true);
    }, []);

    const selectedSourceOptions = useMemo(
        () => sourceOptions.filter((option) => form.sourceLists.some((entry) => Number(entry.id) === Number(option.id))),
        [form.sourceLists, sourceOptions],
    );

    return (
        <Stack spacing={3}>
            {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Box>
                                <Typography variant="h6">{t('Reusable authorization lists')}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t('Create gate lists for license plates or persons, compose larger lists from smaller ones, and import values from CSV files.')}
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                {activeType === 'license_plate' && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<FileUploadIcon />}
                                        onClick={() => {
                                            setForm({ ...emptyForm, type: 'license_plate' });
                                            setImportOpen(true);
                                        }}
                                    >
                                        {t('Import CSV')}
                                    </Button>
                                )}
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                                    {t('Add list')}
                                </Button>
                                <Tooltip title={t('Show API usage details')}>
                                    <IconButton onClick={() => setShowApiInfo((current) => !current)}>
                                        <InfoOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>

                        <Collapse in={showApiInfo}>
                            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} onClose={() => setShowApiInfo(false)}>
                                <Stack spacing={1}>
                                    <Typography variant="body2">
                                        {t('Each list has a numeric ID shown in the table. API clients can authenticate through the access-token endpoint, read lists by ID, and update list contents through the documented JSON endpoints.')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        POST {apiGuide.access_token_url || '/api/access-token'} | GET {apiGuide.list_item_url_template || '/api/gpio-access-lists/__LIST_ID__'}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {apiGuide.documentation_url && (
                                            <Button
                                                component={Link}
                                                href={apiGuide.documentation_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                size="small"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                            >
                                                {t('Open API docs')}
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Alert>
                        </Collapse>

                        <Tabs value={activeType} onChange={(event, value) => {
                            setActiveType(value);
                            setTableSearch('');
                            fetchLists({ page: 1, search: '', type: value });
                        }}>
                            <Tab value="license_plate" label={t('License Plate Lists')} />
                            <Tab value="person" label={t('Person Lists')} />
                        </Tabs>

                        <TextField
                            label={activeType === 'license_plate' ? t('Search by list name or number plate') : t('Search by list name or person')}
                            value={tableSearch}
                            onChange={(event) => {
                                const value = event.target.value;
                                setTableSearch(value);
                                fetchLists({ page: 1, search: value, type: activeType });
                            }}
                            helperText={activeType === 'license_plate'
                                ? t('Search runs in backend and matches list names plus stored number plates and metadata JSON.')
                                : t('Search runs in backend and filters person lists by list fields and linked persons.')}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('List')}</TableCell>
                                <TableCell>{t('Description')}</TableCell>
                                <TableCell>{t('Values')}</TableCell>
                                <TableCell>{t('Includes')}</TableCell>
                                <TableCell align="right">{t('Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5}>
                                        <Typography variant="body2" color="text.secondary">
                                            {loading ? t('Loading...') : t('No access lists yet for this tab.')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row) => {
                                const valueCount = row.type === 'person'
                                    ? (row.person_ids?.length || 0)
                                    : (row.license_plate_entries?.length || row.values?.length || 0);
                                const sourceLists = Array.isArray(row.source_lists) ? row.source_lists.map((source) => ({
                                    ...source,
                                    name: rows.find((entry) => entry.id === source.id)?.name || `#${source.id}`,
                                })) : [];

                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Stack spacing={0.5}>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <Typography variant="subtitle2">{row.name}</Typography>
                                                    <Chip size="small" label={`ID ${row.id}`} />
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.type === 'person' ? t('Person list') : t('License plate list')}
                                                </Typography>
                                                {row.device_name && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.device_name}
                                                    </Typography>
                                                )}
                                                {row.assigned_person_name && row.type === 'license_plate' && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {t('Person')}: {row.assigned_person_name}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{row.description || ' - '}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={t(`${valueCount} values`)} />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {sourceLists.length > 0
                                                    ? sourceLists.map((source) => (
                                                        <Chip
                                                            key={`${row.id}-${source.id}`}
                                                            size="small"
                                                            label={sourceRestrictionsLabel(source)}
                                                        />
                                                    ))
                                                    : <Typography variant="caption" color="text.secondary">{t('None')}</Typography>}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => openViewer(row)}>{t('View')}</Button>
                                            <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(row)}>{t('Edit')}</Button>
                                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteList(row)}>{t('Delete')}</Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
                <TablePagination
                    component="div"
                    count={meta.total || rows.length}
                    page={Math.max(0, (meta.current_page || 1) - 1)}
                    onPageChange={(event, page) => fetchLists({ page: page + 1, search: tableSearch, type: activeType })}
                    rowsPerPage={meta.per_page || 25}
                    onRowsPerPageChange={() => {}}
                    rowsPerPageOptions={[meta.per_page || 25]}
                />
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xl">
                <DialogTitle>{form.id ? t('Edit gate list') : t('Add gate list')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label={t('Type')}
                            value={form.type}
                            onChange={(event) => setForm({
                                ...emptyForm,
                                id: form.id,
                                type: event.target.value,
                                name: form.name,
                                description: form.description,
                            })}
                            SelectProps={{ native: true }}
                        >
                            <option value="license_plate">{t('License plate')}</option>
                            <option value="person">{t('Person')}</option>
                        </TextField>
                        <TextField
                            label={t('Name')}
                            value={form.name}
                            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name?.[0]}
                        />
                        <TextField
                            label={t('Description')}
                            value={form.description}
                            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                            multiline
                            minRows={2}
                        />

                        {form.type === 'license_plate' ? (
                            <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                    <Autocomplete
                                        fullWidth
                                        options={personOptions}
                                        value={personOptions.find((person) => String(person.id) === String(form.assignedPersonId || '')) || null}
                                        getOptionLabel={(option) => option.name || `${option.id}`}
                                        onChange={(event, value) => setForm((current) => ({ ...current, assignedPersonId: value ? String(value.id) : '' }))}
                                        renderInput={(params) => <TextField {...params} label={t('Optional person')} />}
                                    />
                                    {endpoints.personCreate && (
                                        <Button
                                            component={Link}
                                            href={endpoints.personCreate}
                                            target="_blank"
                                            rel="noreferrer"
                                            variant="outlined"
                                            startIcon={<PersonAddIcon />}
                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                            sx={{ alignSelf: { md: 'flex-start' } }}
                                        >
                                            {t('Add person')}
                                        </Button>
                                    )}
                                </Stack>

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            {t('Quick add license plates')}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '44px minmax(0, 1fr)',
                                                border: '1px solid rgba(0,0,0,0.23)',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                bgcolor: '#fff',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    bgcolor: 'rgba(0,0,0,0.03)',
                                                    borderRight: '1px solid rgba(0,0,0,0.12)',
                                                    color: 'text.secondary',
                                                    fontFamily: 'monospace',
                                                    fontSize: 12,
                                                    lineHeight: '24px',
                                                    py: 1.5,
                                                    px: 1,
                                                    whiteSpace: 'pre',
                                                    textAlign: 'right',
                                                    userSelect: 'none',
                                                }}
                                            >
                                                {Array.from({ length: Math.max(12, (form.quickAddText.match(/\n/g)?.length || 0) + 1) }, (_, index) => index + 1).join('\n')}
                                            </Box>
                                            <Box
                                                component="textarea"
                                                value={form.quickAddText}
                                                onChange={(event) => setForm((current) => ({ ...current, quickAddText: event.target.value }))}
                                                spellCheck={false}
                                                sx={{
                                                    minHeight: 324,
                                                    border: 0,
                                                    outline: 0,
                                                    resize: 'vertical',
                                                    width: '100%',
                                                    fontFamily: 'monospace',
                                                    fontSize: 14,
                                                    lineHeight: '24px',
                                                    p: 1.5,
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            {t('One value per line. Use this to add many plates quickly, then fill in metadata below.')}
                                        </Typography>
                                    </Box>
                                    <Stack spacing={1} sx={{ minWidth: { md: 240 } }}>
                                        <Button variant="outlined" onClick={addQuickEntries}>
                                            {t('Add typed plates')}
                                        </Button>
                                        <Button variant="outlined" onClick={() => setShowExistingPlates((current) => !current)}>
                                            {showExistingPlates ? t('Hide existing license plates') : t('Add from existing license plates')}
                                        </Button>
                                    </Stack>
                                </Stack>

                                {showExistingPlates && (
                                    <Autocomplete
                                        multiple
                                        options={licensePlateOptions}
                                        value={licensePlateOptions.filter((option) => form.licensePlateEntries.some((entry) => entry.plate === option.plate))}
                                        getOptionLabel={(option) => option.plate || `${option.id}`}
                                        onChange={(event, value) => {
                                            const selectedEntries = value.map((entry) => ({
                                                plate: entry.plate,
                                                note: entry.note,
                                                contract: entry.contract,
                                                active: entry.active,
                                            }));
                                            setForm((current) => ({
                                                ...current,
                                                licensePlateEntries: mergeEntries(current.licensePlateEntries, selectedEntries),
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('Existing license plates')}
                                                helperText={t('Pick known plates from the global directory and enrich them inside this list.')}
                                            />
                                        )}
                                    />
                                )}

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2">{t('License plate entries')}</Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => setForm((current) => ({
                                            ...current,
                                            licensePlateEntries: [...current.licensePlateEntries, emptyPlateEntry()],
                                        }))}
                                    >
                                        {t('Add entry')}
                                    </Button>
                                </Stack>

                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('Plate')}</TableCell>
                                                <TableCell>{t('Contract')}</TableCell>
                                                <TableCell>{t('Capacity')}</TableCell>
                                                <TableCell>{t('Active')}</TableCell>
                                                <TableCell>{t('Note')}</TableCell>
                                                <TableCell align="right">{t('Actions')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {form.licensePlateEntries.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={6}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('No license plates yet. Add one manually, import from CSV, or pick from the existing directory.')}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {form.licensePlateEntries.map((entry, index) => (
                                                <TableRow key={`entry-${index}`}>
                                                    <TableCell sx={{ minWidth: 160 }}>
                                                        <TextField
                                                            fullWidth
                                                            value={entry.plate}
                                                            onChange={(event) => setPlateEntry(index, { plate: normalizePlate(event.target.value) })}
                                                            placeholder={t('B123ABC')}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 180 }}>
                                                        <TextField
                                                            fullWidth
                                                            value={entry.contract}
                                                            onChange={(event) => setPlateEntry(index, { contract: event.target.value })}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 140 }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            inputProps={{ min: 0 }}
                                                            label={t('Capacity')}
                                                            value={entry.parking_slots ?? ''}
                                                            onChange={(event) => setPlateEntry(index, { parking_slots: normalizeSeatValue(event.target.value) })}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={entry.active !== false}
                                                            onChange={(event) => setPlateEntry(index, { active: event.target.checked })}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 280 }}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            label={t('Note')}
                                                            value={entry.note}
                                                            onChange={(event) => setPlateEntry(index, { note: event.target.value })}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => setForm((current) => ({
                                                                ...current,
                                                                licensePlateEntries: current.licensePlateEntries.filter((item, itemIndex) => itemIndex !== index),
                                                            }))}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </>
                        ) : (
                            <>
                                <TextField
                                    select
                                    label={t('Device')}
                                    value={form.deviceId || ''}
                                    onChange={(event) => setForm((current) => ({
                                        ...current,
                                        deviceId: event.target.value,
                                        personIds: [],
                                        sourceLists: [],
                                    }))}
                                    SelectProps={{ native: true }}
                                    error={Boolean(formErrors.device_id)}
                                    helperText={formErrors.device_id?.[0] || t('Person lists are device-specific. Pick the device first, then choose persons from that device.')}
                                >
                                    <option value="">{t('Select device')}</option>
                                    {devices.map((device) => (
                                        <option key={`device-${device.id}`} value={device.id}>{device.name}</option>
                                    ))}
                                </TextField>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                    <Autocomplete
                                        multiple
                                        fullWidth
                                        options={filteredPersons}
                                        value={filteredPersons.filter((person) => form.personIds.includes(person.id))}
                                        getOptionLabel={(option) => option.name || `${option.id}`}
                                        onChange={(event, value) => setForm((current) => ({ ...current, personIds: value.map((entry) => entry.id) }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('Persons')}
                                                helperText={t('Only persons from the selected device are available here.')}
                                            />
                                        )}
                                    />
                                    {endpoints.personCreate && (
                                        <Button
                                            component={Link}
                                            href={endpoints.personCreate}
                                            target="_blank"
                                            rel="noreferrer"
                                            variant="outlined"
                                            startIcon={<PersonAddIcon />}
                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                            sx={{ alignSelf: { md: 'flex-start' } }}
                                        >
                                            {t('Add person')}
                                        </Button>
                                    )}
                                </Stack>
                            </>
                        )}

                        <Autocomplete
                            multiple
                            options={sourceOptions}
                            value={selectedSourceOptions}
                            getOptionLabel={(option) => option.name || `${option.id}`}
                            onChange={(event, value) => setForm((current) => ({
                                ...current,
                                sourceLists: value.map((entry) => {
                                    const existing = current.sourceLists.find((source) => Number(source.id) === Number(entry.id));
                                    return existing || { id: Number(entry.id), enabled: false, parkingSlots: '' };
                                }),
                            }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('Included lists')}
                                    helperText={form.type === 'person'
                                        ? t('Person lists can only include other lists from the same device.')
                                        : t('Compose this list from other reusable lists of the same type.')}
                                />
                            )}
                        />

                        {form.type === 'license_plate' && form.sourceLists.length > 0 && (
                            <Stack spacing={1}>
                                <Typography variant="subtitle2">{t('Included list restrictions')}</Typography>
                                {form.sourceLists.map((source) => {
                                    const list = rows.find((entry) => Number(entry.id) === Number(source.id));

                                    return (
                                        <Stack key={`source-${source.id}`} direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                                            <Typography sx={{ minWidth: 240 }}>{list?.name || `#${source.id}`}</Typography>
                                            <FormControlLabel
                                                control={(
                                                    <Switch
                                                        checked={Boolean(source.enabled)}
                                                        onChange={(event) => setForm((current) => ({
                                                            ...current,
                                                            sourceLists: current.sourceLists.map((entry) => (
                                                                Number(entry.id) === Number(source.id)
                                                                    ? { ...entry, enabled: event.target.checked, parkingSlots: event.target.checked ? entry.parkingSlots : '' }
                                                                    : entry
                                                            )),
                                                        }))}
                                                    />
                                                )}
                                                label={t('Capacity limit')}
                                            />
                                            {source.enabled && (
                                                <TextField
                                                    type="number"
                                                    inputProps={{ min: 0, max: 1000 }}
                                                    label={t('Capacity')}
                                                    value={source.parkingSlots}
                                                    onChange={(event) => setForm((current) => ({
                                                        ...current,
                                                        sourceLists: current.sourceLists.map((entry) => (
                                                            Number(entry.id) === Number(source.id)
                                                                ? { ...entry, parkingSlots: normalizeSeatValue(event.target.value) }
                                                                : entry
                                                        )),
                                                    }))}
                                                    sx={{ maxWidth: 180 }}
                                                />
                                            )}
                                            <Tooltip title={t('This limits how many parking slots from the included list are available in this composed list.')}>
                                                <IconButton size="small">
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={saveList} disabled={saving}>
                        {form.id ? t('Save changes') : t('Create list')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('Import license plate list')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label={t('Name')} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
                        <TextField label={t('Description')} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} multiline minRows={2} />
                        <Alert severity="info">
                            {t('Import works only for license plates. The file must contain exactly one populated column and one plate per row. Optional header examples: plate, license_plate, number.')}
                        </Alert>
                        <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('Example file')}</Typography>
                            <Box component="div" sx={{ mt: 1, fontSize: 12, fontFamily: 'monospace', lineHeight: 1.6, whiteSpace: 'pre' }}>
                                {['plate', 'B101ABC', 'B202XYZ', 'CJ03TEST'].map((line) => (
                                    <Box component="div" key={line}>{line}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Button variant="outlined" component="label">
                            {t('Choose CSV file')}
                            <input hidden ref={importFileRef} type="file" accept=".csv,.txt" />
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                            {t('Rows with multiple populated columns are ignored so the import remains predictable.')}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setImportOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={importList} disabled={saving}>{t('Import')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{viewerList ? viewerList.name : t('List contents')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={t('Search in list')}
                            value={viewerSearch}
                            onChange={(event) => {
                                setViewerSearch(event.target.value);
                                setViewerPage(1);
                            }}
                        />
                        <Box sx={{ columnCount: { xs: 1, sm: 2, md: 3 }, columnGap: 2 }}>
                            {viewerPageItems.map((entry, index) => (
                                <Box
                                    key={`viewer-${viewerList?.id || 'x'}-${entry.id ?? entry.label}-${index}`}
                                    sx={{ breakInside: 'avoid', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, px: 1.5, py: 1, mb: 1, backgroundColor: '#fff' }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.label}</Typography>
                                    {entry.meta && <Typography variant="caption" color="text.secondary">{entry.meta}</Typography>}
                                </Box>
                            ))}
                        </Box>
                        <Stack alignItems="center">
                            <Pagination
                                count={viewerPageCount}
                                page={Math.min(viewerPage, viewerPageCount)}
                                onChange={(event, page) => setViewerPage(page)}
                                color="primary"
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewerOpen(false)}>{t('Close')}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
